import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { errorWasShown } from "../lib/errorShown";
import { isApiError } from "./client";

let onUnauthorized: (() => void) | null = null;

/** The router registers this so an expired session sends the user to /login. */
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

function handleError(err: unknown) {
  if (isApiError(err) && err.status === 401) onUnauthorized?.();
}

let onUnsaidFailure: ((message: string) => void) | null = null;

/** The app shell registers this (a toast): what a failed change says when nothing else on the page did. */
export function setMutationErrorFallback(fn: ((message: string) => void) | null) {
  onUnsaidFailure = fn;
}

/** Changes a person started (a click, a key): background ones (a refresh on load, a timer) fail without a toast. */
const byPerson = new WeakSet<object>();
const personStarted = (mutation: object) => {
  if (typeof navigator === "undefined" || !navigator.userActivation || navigator.userActivation.isActive) byPerson.add(mutation);
};

/**
 * The fallback for a change that failed silently: once the page has had its moment to say so (a toast, a FormError,
 * the message under a button), a failure nobody said gets a toast. Only for changes a person started, and not for a
 * hook that handles its own failures (`onError`, usually putting an optimistic change back), one marked
 * `meta: { quiet: true }`, field errors (the form shows them by their fields), or a signed-out session.
 */
function mutationError(err: unknown, mutation: { options: { onError?: unknown }; meta?: Record<string, unknown> }) {
  handleError(err);
  if (!byPerson.has(mutation) || mutation.options.onError || mutation.meta?.quiet) return;
  if (err instanceof DOMException && err.name === "AbortError") return;
  if (isApiError(err) && (err.status === 401 || Object.keys(err.fields).length)) return;
  const message = isApiError(err) ? err.message : "Something went wrong. Try again in a moment.";
  const since = Date.now();
  setTimeout(() => {
    if (!errorWasShown(message, since)) onUnsaidFailure?.(message);
  }, 400);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleError }),
  mutationCache: new MutationCache({ onMutate: (_v, mutation) => personStarted(mutation), onError: (err, _v, _c, mutation) => mutationError(err, mutation) }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Only retry when the network or API is down; a 4xx won't change on retry.
      retry: (count, err) => isApiError(err) && err.isNetwork && count < 2,
      retryDelay: (n) => Math.min(1000 * 2 ** n, 5000),
    },
    mutations: { retry: false },
  },
});
